import { formatReleaseTime } from '@home/components/NavHeader/sreachContext/utils';
import { formatDateStr } from '@intelligence/v3/utils/date';
import { getCountryByCode, getRegionByCode, getRegionByCountryCode } from '@intelligence/utils/commonUtils';
import { LANGUAGE_EN } from '@home/_config/config';
import { DataCalculation } from '@intelligence/v3/enums/download';
import { DOWNLOAD_CFG_KEY } from '@intelligence/v3/constants';

const formatSensorTowerTags = (tagKey: string, data?: IRecord | null): string => {
  const sensorTowerTags = data?.sensortower_tag || [];
  const sensorTowerTag = sensorTowerTags?.find((tag: any) => {
    return tag?.tag_name === tagKey;
  });
  const subTags = sensorTowerTag?.Tags || [];
  if (!Array.isArray(subTags) || subTags.length === 0) {
    return '';
  }

  return subTags
    .map((subTag) => {
      return subTag?.tag_name || '';
    })
    .join(', ');
};

export const buildPublisherConfigs = (params?: IRecord, regionOptions?: any[] | null) => {
  const isCompanyShow = params?.company_show === 1;

  return [
    {
      title: 'Company Name',
      key: 'publisher',
      when: () => isCompanyShow,
    },
    {
      title: 'Company HQ',
      key: (item: any) => {
        return getCountryByCode(item?.publisher_country_abbre, regionOptions!)?.country_name_en || '';
      },
      when: () => isCompanyShow,
    },
    {
      title: 'Company HQ Name',
      key: (item: any) => {
        return getCountryByCode(item?.publisher_country_abbre, regionOptions!)?.country_name_ch || '';
      },
      when: () => isCompanyShow,
    },
    {
      title: 'Parent Company',
      key: 'parent_publisher',
      when: () => isCompanyShow,
    },
  ];
};

export const buildGenreConfigs = (params?: IRecord | null) => {
  const isGenreShow = params?.genre_show === 1;
  const showGenreSource = params?.genre_source || 'iegg';
  const isIEGGGenreShow = showGenreSource === 'iegg';
  const isAnnieGenreShow = showGenreSource === 'annie';
  const isSensorTowerGenreShow = showGenreSource === 'sensortower';

  return [
    {
      title: 'IEGG Main Genre',
      key: 'iegg_genre',
      when: () => isGenreShow && isIEGGGenreShow,
    },
    {
      title: 'IEGG Sub Genre',
      key: 'iegg_sub_genre',
      when: () => isGenreShow && isIEGGGenreShow,
    },
    {
      title: 'App Annie Main Genre',
      key: 'genre',
      when: () => isGenreShow && isAnnieGenreShow,
    },
    {
      title: 'App Annie Sub Genre',
      key: 'sub_genre',
      when: () => isGenreShow && isAnnieGenreShow,
    },
    {
      title: 'Sensor Tower Main Genre',
      key: 'sensortower_genre',
      when: () => isGenreShow && isSensorTowerGenreShow,
    },
    {
      title: 'Sensor Tower Sub Genre',
      key: 'sensortower_sub_genre',
      when: () => isGenreShow && isSensorTowerGenreShow,
    },
  ];
};

export const buildRegionCountryConfigs = (params?: IRecord | null, regionOptions?: any[] | null) => {
  const isGroupByRegion = params?.[DOWNLOAD_CFG_KEY.REGION_METHOD] === DataCalculation.SeparateByRegion;
  const isGroupByMarket = params?.[DOWNLOAD_CFG_KEY.REGION_METHOD] === DataCalculation.SeparateByCountry;

  return [
    {
      title: 'Region_Chinese',
      key: (item: any) => {
        const itemRegion = item?.region || '';
        const itemMarket = item?.market || '';
        if (isGroupByRegion) return getRegionByCode(itemRegion, regionOptions)?.area_name_ch || itemRegion || '';
        if (isGroupByMarket) return getRegionByCountryCode(itemMarket, regionOptions)?.area_name_ch || itemMarket || '';
        return item?.market || item?.region || '';
      },
    },
    {
      title: 'Region_English',
      key: (item: any) => {
        const itemRegion = item?.region || '';
        const itemMarket = item?.market || '';
        if (isGroupByRegion) return getRegionByCode(itemRegion, regionOptions)?.area_name_en || itemRegion || '';
        if (isGroupByMarket) return getRegionByCountryCode(itemMarket, regionOptions)?.area_name_en || itemMarket || '';
        return item?.market || item?.region || '';
      },
    },
    {
      title: 'Country Name',
      key: (item: any) => getCountryByCode(item?.market, regionOptions)?.country_name_en || item?.market || '',
    },
    {
      title: 'Country name in Abbreviation',
      key: (item: any) => getCountryByCode(item?.market, regionOptions)?.country_name_ch || item?.market || '',
    },
  ];
};

export const buildReleaseDateConfigs = (params?: IRecord | null) => {
  const isReleaseDateShow = params?.release_date_show === 1;

  return [
    {
      title: 'Release Date',
      key: (item: any) => {
        const isSpecialReleaseTime = item?.is_special_release_time;
        const releaseTime = item?.release_time;
        const releaseTimeEN = item?.release_time_en;
        const releaseTimeCN = item?.release_time_cn;
        return formatReleaseTime(LANGUAGE_EN, {
          isSpecialReleaseTime,
          releaseTime,
          releaseTimeEN,
          releaseTimeCN,
        });
      },
      when: () => isReleaseDateShow,
    },
  ];
};

export const buildDateConfigs = (params?: IRecord | null) => {
  const granularity = params?.granularity || 'daily';
  const isGroupByDate = params?.downlaod_date_method !== DataCalculation.Aggregated;

  return [
    {
      title: 'Date',
      key: (item: any) => {
        const itemDate = item?.date || '';
        return granularity === 'monthly' ? formatDateStr(itemDate, 'YYYY-MM') : itemDate;
      },
      when: () => isGroupByDate,
    },
  ];
};

export const buildSensorTowerTagsConfigs = (params?: IRecord | null) => {
  const isSensorTowerTagsShow = params?.sensortower_tags_show === 1;
  return [
    {
      title: 'Art Style',
      key: (item?: any) => {
        return formatSensorTowerTags('Art Style', item);
      },
      when: isSensorTowerTagsShow,
    },
    {
      title: 'Camera POV',
      key: (item?: any) => {
        return formatSensorTowerTags('Camera POV', item);
      },
      when: isSensorTowerTagsShow,
    },
    {
      title: 'Product Model',
      key: (item?: any) => {
        return formatSensorTowerTags('Product Model', item);
      },
      when: isSensorTowerTagsShow,
    },
    {
      title: 'Setting',
      key: (item?: any) => {
        return formatSensorTowerTags('Setting', item);
      },
      when: isSensorTowerTagsShow,
    },
    {
      title: 'Tag',
      key: (item?: any) => {
        return formatSensorTowerTags('Tag', item);
      },
      when: isSensorTowerTagsShow,
    },
    {
      title: 'Theme',
      key: (item?: any) => {
        const sensorTowerTags = item?.sensortower_tag || [];
        const sensorTowerTag = sensorTowerTags?.find((tag: any) => {
          return tag?.tag_name === 'Theme';
        });
        const subTags = sensorTowerTag?.Tags || [];
        if (!Array.isArray(subTags) || subTags.length === 0) {
          return '';
        }

        const list: string[] = [];
        for (const subTag of subTags) {
          const ssubTags = subTag?.Tags || [];
          if (!Array.isArray(ssubTags) || ssubTags.length === 0) {
            continue;
          }
          for (const ssubTag of ssubTags) {
            list.push(`${subTag?.tag_name}|${ssubTag?.tag_name}`);
          }
        }

        return list.join(', ');
      },
      when: isSensorTowerTagsShow,
    },
    {
      title: 'Monetization',
      key: (item?: any) => {
        return formatSensorTowerTags('Monetization', item);
      },
      when: isSensorTowerTagsShow,
    },
  ];
};
